import { useMemo, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { setMaxDate, listType } from 'common/utils';
import {
  Headings,
  Paragraph,
  InputField,
  Label,
  ErrorMessage,
  HelpIcon,
  Checkbox,
  Button,
  DatePicker,
  PrintIcon,
  ChevronLeftIcon,
  TextArea,
  Link,
  ModalMultiUse,
} from '@sentara-health/optimahealth_web';
import {
  DateFormatRegex,
  EmailRegex,
  getMaxLength,
  specialCharacterFormat,
  DeviceTypes,
  useDeviceType,
  NavigateConst,
} from 'common';
interface FormDataProps {
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  disclosure: DisclosureProps;
  expiration: ExpirationProps;
  date: string;
  purpose: PurposeProps;
  disclosureOtherText: string;
  abuseAuthorization: AbuseAuthorizationProps;
  acknowledgement: boolean;
}
interface DisclosureProps {
  medicalClaims: boolean;
  accountInfo: boolean;
  clinicalInfo: boolean;
  primaryPhysician: boolean;
  allAbove: boolean;
}
interface ExpirationProps {
  disenrollmentExp: boolean;
  customExp: boolean;
  date: string;
}
interface PurposeProps {
  disclosureRequest: boolean;
  disclosureOther: boolean;
  disclosureOtherText: string;
}
interface AbuseAuthorizationProps {
  substanceAbuseYes: boolean;
  substanceAbuseNo: boolean;
}

const CSRProxyForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const getDefaultValues = () => ({
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    disclosure: {
      medicalClaims: false,
      accountInfo: false,
      clinicalInfo: false,
      primaryPhysician: false,
      allAbove: false,
    },
    expiration: {
      disenrollmentExp: false,
      customExp: false,
      date: '',
    },
    date: '',
    purpose: {
      disclosureRequest: false,
      disclosureOther: false,
      disclosureOtherText: '',
    },
    disclosureOtherText: '',
    abuseAuthorization: {
      substanceAbuseYes: false,
      substanceAbuseNo: false,
    },
    acknowledgement: false,
  });
  const {
    handleSubmit,
    register,
    unregister,
    setValue,
    clearErrors,
    getValues,
    control,
    formState: { errors, isValid },
  } = useForm<FormDataProps>({
    mode: 'onBlur',
    defaultValues: getDefaultValues()
  });
  //device type detection for responsive design of pdf viewer
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;

  //state to handle modals for acknowledgement and purpose of disclosure sections
  const [isAcknowledgementModalOpen, setIsAcknowledgementModal] = useState(false);
  const [isSubstanceUseRecordsModalOpen, setIsSubstanceUseRecordsModal] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);


  //fields to watch for validation before submission
  const expiration = useWatch({ control, name: 'expiration' });
  const purpose = useWatch({ control, name: 'purpose' });
  const abuseAuthorization = useWatch({ control, name: 'abuseAuthorization' });
  const acknowledgement = useWatch({ control, name: 'acknowledgement' });

  // objects and arrays to generate form fields and options dynamically, also used for validation and error handling
  const formDataPersonal: Record<string, any> = useMemo(() => ({
    firstName: {
      id: 'firstName',
      colClass: 'col-md-6 col-12 mb-3',
      required: true,
      label: t('common.firstName'),
      maxLength: getMaxLength.name,
      error: errors.firstName,
      restrictInputValue: specialCharacterFormat,
    },
    lastName: {
      id: 'lastName',
      colClass: 'col-md-6 col-12 mb-3',
      required: true,
      label: t('common.lastName'),
      maxLength: getMaxLength.name,
      error: errors.lastName,
      restrictInputValue: specialCharacterFormat,
    },
  }), [errors.firstName, errors.lastName, t]);

  const formDataEmail: Record<string, any> = useMemo(() => ({
    email: {
      id: 'email',
      colClass: 'col-md-6 col-12 mb-3',
      required: true,
      label: t('common.emailAddress'),
      maxLength: getMaxLength.email,
      error: errors.email,
      restrictInputValue: EmailRegex,
    },
    confirmEmail: {
      id: 'confirmEmail',
      colClass: 'col-md-6 col-12 mb-3',
      required: true,
      label: t('common.emailAddressConfirmation'),
      maxLength: getMaxLength.email,
      error: errors.confirmEmail,
      restrictInputValue: EmailRegex,
    },
  }), [errors.email, errors.confirmEmail, t]);

  const formDataDisclosures = useMemo(() => ([
    {
      id: 'medicalClaims',
      colClass: 'col-md-6 col-12 mb-3',
      label: t('csrGuardian.medicalClaimsAuth'),
    },
    {
      id: 'accountInfo',
      colClass: 'col-md-6 col-12 mb-3',
      label: t('csrGuardian.accountInformation'),
    },
    {
      id: 'clinicalInfo',
      colClass: 'col-md-6 col-12 mb-3',
      label: t('csrGuardian.clinicalInformation'),
    },
    {
      id: 'primaryPhysician',
      colClass: 'col-md-6 col-12 mb-3',
      label: t('csrGuardian.primaryPhysician'),
    },
    {
      id: 'allAbove',
      colClass: 'col-md-6 col-12 mb-3',
      label: t('csrGuardian.allTheAbove'),
    },
  ]), [t]);

  const formDataExpiration = useMemo(() => ([
    {
      id: 'disenrollmentExp',
      colClass: 'd-flex align-items-top col-xl-6 col-12 mb-xl-0 mb-3',
      label: t('csrGuardian.coverageEnds'),
    },
    {
      id: 'customExp',
      label: t('csrGuardian.customExpirationDate'),
      colClass: 'd-flex align-items-top col-xl-6 col-12 mb-xl-0 mb-2',
    },
    {
      id: 'date',
      label: t('csrGuardian.expirationDate'),
      required: true,
      placeholder: t('common.selectDate'),
      maxLength: getMaxLength.date,
      error: errors.date,
      restrictInputValue: DateFormatRegex,
    },
  ]), [errors.date, t]);

  const formDataPurpose = useMemo(() => ([
    {
      id: 'disclosureRequest',
      colClass: 'd-flex align-items-center col-xl-6 col-12 mb-xl-2 mb-3',
      label: t('csrGuardian.atMyRequest'),
    },
    {
      id: 'disclosureOther',
      label: t('csrGuardian.others'),
      colClass: 'd-flex align-items-center col-xl-6 col-12 mb-2',
    },
    {
      id: 'disclosureOtherText',
      label: t('csrGuardian.otherReason'),
      required: true,
      placeholder: t('csrGuardian.stateReason') ?? '',
      maxLength: getMaxLength.textArea,
      error: errors.disclosureOtherText,
      restrictInputValue: undefined,
    },
  ]), [errors.disclosureOtherText, t]);

  const formDataAbuseAuthorization = useMemo(() => ([
    {
      id: 'substanceAbuseYes',
      colClass: 'd-flex align-items-top col-12 mb-3',
      label:
        <Trans i18nKey="common.radioAgree"
          values={{ fieldName: t('common.authorizationForSubstanceUseRecords') }}
          components={{
            link1: <Link
              onClick={() => { setIsSubstanceUseRecordsModal(true) }}
              className="text-decoration-underline"
            />
          }} />,
      checked: false,
    },
    {
      id: 'substanceAbuseNo',
      label: t('csrGuardian.noAuthorize'),
      colClass: 'd-flex align-items-top col-12 mb-md-3 mb-4',
      checked: false,
    },
  ]), [t]);

  const formDataModals = useMemo(() => ({
    purposeHelpModal: {
      id: 'helpModal',
      title: undefined,
      content: t('csrGuardian.purposeHelpModalContent'),
      variant: 'default',
      iconClose: true, // to show close on modal body
      isMobile,
      actionOpen: () => isHelpModalOpen,
      actionClose: () => setIsHelpModalOpen(false),
    },
    substanceModal: {
      id: 'substanceModal',
      title: t('csrGuardian.purposeOfDisclosure'),
      content:
        <Paragraph
          level={3}
          text={<Trans
            i18nKey="csrGuardian.substanceModalContent"
            components={{
              strong: <strong />,
            }} />}
          className="lineBrake mb-0" />,
      variant: 'fullScreenSmDown',
      isMobile,
      iconClose: false,
      actionOpen: () => isSubstanceUseRecordsModalOpen,
      actionClose: () => setIsSubstanceUseRecordsModal(false),
    },
    acknowledgementModal: {
      id: 'acknowledgementModal',
      title: t('csrGuardian.acknowledgementTitle'),
      content: listType(t('csrGuardian.acknowledgementModalContent'), 'lineBrake, decimalList'),
      variant: 'fullScreenSmDown',
      isMobile,
      iconClose: false,
      actionOpen: () => isAcknowledgementModalOpen,
      actionClose: () => setIsAcknowledgementModal(false),
    },
  }), [t, isAcknowledgementModalOpen, isMobile, isSubstanceUseRecordsModalOpen, isHelpModalOpen]);

  //field options to generate checkboxes and radio buttons dynamically, also used for validation and error handling
  const individualDisclosureFields = useMemo(
    () => formDataDisclosures.filter((field) => field.id !== 'allAbove'),
    [formDataDisclosures]
  );

  const expirationOptions = useMemo(() => formDataExpiration.reduce((options, field) => {
    if (field.id !== 'date') {
      options[field.id] = expiration?.[field.id as keyof ExpirationProps] ? true : false;
    }
    return options;
  }, {} as Record<string, boolean>), [expiration, formDataExpiration]);

  const purposeOptions = useMemo(() => formDataPurpose.reduce((options, field) => {
    if (field.id !== 'disclosureOtherText') {
      options[field.id] = purpose?.[field.id as keyof PurposeProps] ? true : false;
    }
    return options;
  }, {} as Record<string, boolean>), [purpose, formDataPurpose]);

  const abuseAuthorizationOptions = useMemo(() => formDataAbuseAuthorization.reduce((options, field) => {
    options[field.id] = abuseAuthorization?.[field.id as keyof AbuseAuthorizationProps] ? true : false;
    return options;
  }, {} as Record<string, boolean>), [abuseAuthorization, formDataAbuseAuthorization]);

  // functions to handle change events 
  function handleAllAboveSelection(isChecked: boolean) {
    const nextDisclosure = individualDisclosureFields.reduce((acc, option) => {
      acc[option.id as keyof DisclosureProps] = isChecked;
      return acc;
    }, {
      ...(getValues('disclosure') as DisclosureProps),
      allAbove: isChecked,
    } as DisclosureProps);

    setValue('disclosure' as keyof FormDataProps, nextDisclosure as any, {
      shouldValidate: true,
    });

    const hasAnyDisclosureSelected = Object.values(nextDisclosure).some((value) => value);
    if (hasAnyDisclosureSelected && errors.disclosure) {
      clearErrors('disclosure');
    }
  };

  function handleIndividualDisclosureChange(isChecked: boolean, fieldId: keyof DisclosureProps) {
    const currentDisclosure = getValues('disclosure') as DisclosureProps;
    const nextDisclosure = {
      ...currentDisclosure,
      [fieldId]: isChecked,
    } as DisclosureProps;

    const allChecked = individualDisclosureFields.every(
      (option) => Boolean(nextDisclosure[option.id as keyof DisclosureProps])
    );
    nextDisclosure.allAbove = allChecked;
    setValue('disclosure' as keyof FormDataProps, nextDisclosure as any, { shouldValidate: true });

    const hasAnyDisclosureSelected = Object.values(nextDisclosure).some((value) => value);
    if (hasAnyDisclosureSelected && errors.disclosure) {
      clearErrors('disclosure');
    }
  };

  const resetAbuseAuthorizationOptions = (selectedId: string) => {
    formDataAbuseAuthorization.forEach((option) => {
      if (option.id !== selectedId) {
        setValue(`abuseAuthorization.${option.id}` as keyof FormDataProps, false as any);
      }
    });
  };

  // edge cases handling for validation of dynamically generated fields based on the selection of checkboxes and radio buttons
  const edgeCaseForExpirationDate = useMemo(() => ((!expirationOptions.customExp && !expirationOptions.disenrollmentExp) || expirationOptions.customExp) && (!expiration?.date && (Object.values(purposeOptions).some((value) => value) || Object.values(abuseAuthorizationOptions).some((value) => value))), [abuseAuthorizationOptions, expiration?.date, expirationOptions.customExp, expirationOptions.disenrollmentExp, purposeOptions]);
  const edgeCaseForPurpose = useMemo(() => ((!purposeOptions.disclosureRequest && !purposeOptions.disclosureOther) || purposeOptions.disclosureOther) && !purpose?.disclosureOtherText && Object.values((abuseAuthorizationOptions)).some((value) => value), [abuseAuthorizationOptions, purpose?.disclosureOtherText, purposeOptions.disclosureOther, purposeOptions.disclosureRequest]);

  //submit && cancel handlers
  const onSubmit = useCallback(() => {
    //handle form submission, currently just logging the form data to console
    navigate(NavigateConst.profile)

  }, [navigate]);

  const handleCancel = useCallback(() => {
    navigate(NavigateConst.profile);
  }, [navigate]);

  return (
    <>
      {/* buttons to go back to profile and print the form, also used for responsive design of pdf viewer */}
      <div className="d-flex justify-content-between align-items-center col-12 my-md-2">
        <Button
          data-testid="back-to-Authorization"
          iconLeft={<ChevronLeftIcon />}
          variant="icon_btn"
          onClick={handleCancel}
          className="ml-n1"
        >
          {t('common.back')}
        </Button>
        <Button
          data-testid="back-to-Authorization"
          iconLeft={<PrintIcon />}
          variant="icon_btn"
          onClick={(e: { preventDefault: () => void }) => {
            e.preventDefault();
            navigate('/profile');
          }}
        >
          {t('common.print')}
        </Button>
      </div>
      {/* heading and description section of the form */}
      <div className="col-12 my-md-4 my-3">
        <Headings
          level={isMobile ? 2 : 1}
          text={t('profile.designatedRepresentativeTitle')}
          variant="semiBold"
          className="pt-3"
        />
        {isMobile ? null : <hr className="hr-1" />}
      </div>
      <div className="col-xl-7 col-lg-8 col-12 px-md-4 mt-md-5 mt-4 pt-md-0 pt-2">
        <Paragraph
          level={5}
          variant="bold"
          text={t('csrGuardian.formTitle')}
          className="mb-2"
        />
        <Paragraph
          text={t('csrGuardian.formDescription')}
          variant="regular"
          className="mb-md-3 mb-0"
        />
        <hr className="hr-2" />
      </div>
      {/* form section, generated dynamically based on the objects and arrays defined above */}
      <div id="printFormSection"
        className="col-xl-7 col-lg-10 col-12 px-md-4">
        <Paragraph
          level={3}
          text={<Trans i18nKey="csrGuardian.allFieldsRequired" components={{ em: <em /> }} />}
          variant="italic"
        />
        {/* form sections generated dynamically based on the objects and arrays defined above */}
        <form onSubmit={handleSubmit(onSubmit)} data-testid="csrProxyForm">
          {/* personal information section */}
          <div className="row mb-4">
            <div className="col-12">
              <Paragraph
                level={5}
                variant="bold"
                text={t('csrGuardian.designatedRepInfo')}
                className="mb-2"
              />
              <Paragraph
                text={t('csrGuardian.designatedRepDescription')}
                variant="regular"
              />
            </div>
            <div className="col-12">
              <div className="row">
                {Object.keys(formDataPersonal).map((key) => {
                  const field = formDataPersonal[key];
                  return (
                    <div className={field.colClass} key={field.id}>
                      <Label
                        htmlFor={field.id}
                        variant="formLabel"
                        className="mb-1"
                        data-testid={`${field.id}Label`}
                      >
                        {field.label}
                      </Label>
                      <div className="d-flex align-items-center">
                        <InputField
                          id={field.id}
                          type="text"
                          placeholder={t('common.placeholder', { fieldName: field.label.toLowerCase() })}
                          maxLength={field.maxLength}
                          data-testid={`${field.id}Input`}
                          error={field.error}
                          className={field.error ? 'error-border' : ''}
                          {...register(field.id as keyof FormDataProps, {
                            required: t('common.requiredField', { fieldName: field.label.toLowerCase() }),
                            pattern: {
                              value: field.restrictInputValue,
                              message: t('common.validField', { fieldName: field.label.toLowerCase(), fieldFormat: '' }),
                            },
                            onChange: (e) => {
                              clearErrors(field.id as keyof FormDataProps);
                              setValue(field.id as keyof FormDataProps, e.target.value);
                            },
                            validate: field.id
                              ? (value) => {
                                return field.restrictInputValue.test(
                                  String(value)
                                );
                              }
                              : undefined,
                          })}
                        />
                      </div>
                      {field.error ? (
                        <ErrorMessage
                          data-testid={`${field.id}Error`}
                          errors={errors}
                        >{field?.error?.message}</ErrorMessage>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <hr className="my-4" />
          {/* email section */}
          <div className="row mb-4">
            <div className="col-12">
              <Paragraph
                level={5}
                variant="bold"
                text={t('csrGuardian.designatedRepInfo')}
                className="mb-2"
              />
              <Paragraph
                text={t('csrGuardian.designatedRepDescription')}
                variant="regular"
              />
            </div>
            <div className="col-12">
              <div className="row">
                {Object.keys(formDataEmail).map((key) => {
                  const field = formDataEmail[key];
                  return (
                    <div className={field.colClass} key={field.id}>
                      <Label
                        htmlFor={field.id}
                        variant="formLabel"
                        className="mb-1"
                        data-testid={`${field.id}Label`}
                      >
                        {field.label}
                      </Label>
                      <div className="d-flex align-items-center">
                        <InputField
                          id={field.id}
                          type="text"
                          placeholder={t('common.placeholder', { fieldName: field.label.toLowerCase() })}
                          maxLength={field.maxLength}
                          data-testid={`${field.id}Input`}
                          error={field.error}
                          className={field.error ? 'error-border' : ''}
                          {...register(field.id as keyof FormDataProps, {
                            required: t('common.requiredField', { fieldName: field.label.toLowerCase() }),
                            onChange: (e) => {
                              clearErrors(field.id as keyof FormDataProps);
                              setValue(field.id as keyof FormDataProps, e.target.value);
                            },
                            validate: field.id === 'confirmEmail'
                              ? (value) => {
                                const emailValue = getValues('email');
                                if (value !== emailValue) {
                                  return t('common.infoMismatch', { fieldName: t('common.emailAddresses').toLowerCase() });
                                }

                                if (!field.restrictInputValue.test(String(value))) {
                                  return t('common.validField', { fieldName: field.label.toLowerCase(), fieldFormat: '' });
                                }

                                return true;
                              }
                              : field.id
                                ? (value) => {
                                  return field.restrictInputValue.test(String(value))
                                    || t('common.validField', { fieldName: field.label.toLowerCase(), fieldFormat: '' });
                                }
                                : undefined,
                          })}
                        />
                      </div>
                      {field.error ? (
                        <ErrorMessage
                          data-testid={`${field.id}Error`}
                          errors={errors}
                        >{field?.error?.message}</ErrorMessage>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <hr className="my-4" />
          {/* disclosure section */}
          <div className="row mb-4">
            <div className="col-12">
              <Paragraph
                level={5}
                variant="bold"
                text={t('csrGuardian.informationToDisclose')}
                className="my-2"
              />
              <Paragraph
                text={t('csrGuardian.disclosureDescription')}
                variant="regular"
              />
            </div>
            <div className="col-12">
              <div className="row d-flex align-items-top">
                {formDataDisclosures.map((field) => (
                  <div className={`${field.colClass} d-flex align-items-center`} key={field.id}>
                    <Controller
                      name={`disclosure.${field.id}` as keyof FormDataProps}
                      control={control}
                      rules={{
                        validate: () => {
                          const currentDisclosure = getValues('disclosure') as DisclosureProps;
                          const hasAnyDisclosureSelected = Object.values(currentDisclosure).some((value) => value);
                          return hasAnyDisclosureSelected
                            || t('common.requiredSelection', { fieldName: t('csrGuardian.informationToDisclose').toLowerCase() });
                        },
                      }}
                      render={({ field: { value, onBlur } }) => (
                        <Checkbox
                          {...field}
                          level={3}
                          id={field.id}
                          data-testid={`${field.id}Checkbox`}
                          checked={value || false}
                          onBlur={onBlur}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const isChecked = (e.target as HTMLInputElement).checked;
                            if (field.id === 'allAbove') {
                              handleAllAboveSelection(isChecked);
                            } else {
                              handleIndividualDisclosureChange(isChecked, field.id as keyof DisclosureProps);
                            }
                          }}
                        >
                          {field.label}
                        </Checkbox>
                      )}
                    />
                  </div>
                ))}
                {errors.disclosure ? (
                  <div className="col-12 mt-n1">
                    <ErrorMessage
                      data-testid={`disclosureError`}
                      errors={errors}
                    >
                      {t('common.requiredSelection', { fieldName: t('csrGuardian.informationToDisclose').toLowerCase() })}
                    </ErrorMessage>
                  </div>
                ) : null}

              </div>
            </div>
          </div>
          <hr className="my-4" />
          {/* expiration date section */}
          <div className="row mb-4">
            <div className="col-12">
              <Paragraph
                level={5}
                variant="bold"
                text={t('csrGuardian.expirationDate')}
                className="mt-2"
              />
            </div>
            <div className="col-12">
              <div className="d-flex row align-items-top">
                {formDataExpiration.map((field) => (
                  <div className={field.colClass} key={field.id}>
                    <Controller
                      name={`expiration.${field.id}` as keyof FormDataProps}
                      control={control}
                      rules={{
                        required: field.id === 'date'
                          ? expirationOptions.customExp ? t('common.requiredField', { fieldName: field.label.toLowerCase() }) : false
                          : !Object.values(expirationOptions).some((value) => value) ? t('common.requiredSelection', { fieldName: t('csrGuardian.expirationDate').toLowerCase() }) : undefined,
                        pattern: field.restrictInputValue ? {
                          value: field.restrictInputValue,
                          message: t('common.validField', { fieldName: field.label.toLowerCase(), fieldFormat: t('common.dateFormat') }),
                        } : undefined,

                        validate: () => {
                          const hasSelection = expirationOptions.disenrollmentExp || expirationOptions.customExp;
                          return hasSelection || t('common.requiredSelection', { fieldName: t('csrGuardian.expirationDate').toLowerCase() });
                        }
                      }}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <>
                          {field.id === 'date' ?
                            (
                              expirationOptions.customExp ? (
                                <div className="offset-xl-6 flex-1 col-md-6 col-12 csrProxy-cust-1">
                                  <DatePicker
                                    selectedValue={value}
                                    maxLength={field.maxLength}
                                    fieldName={field.id}
                                    maxDate={setMaxDate(0)}
                                    data-testid={`${field.id}Input`}
                                    placeholder={field.placeholder}
                                    updateValue={(updateValue: string) => {
                                      setValue(`expiration.${field.id}` as keyof FormDataProps, updateValue, {
                                        shouldValidate: true,
                                      });
                                      clearErrors(`expiration.${field.id}` as keyof FormDataProps);
                                    }}
                                    errorClass={errors.expiration?.[field.id as keyof ExpirationProps] ? 'error-border' : ''}
                                  />
                                </div>
                              ) : null
                            )
                            : (
                              <Checkbox
                                {...field}
                                level={3}
                                id={field.id}
                                type="radio"
                                data-testid={`${field.id}Checkbox`}
                                checked={value || false}
                                onBlur={onBlur}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const isChecked = (e.target as HTMLInputElement).checked;
                                  onChange(isChecked);
                                  if (isChecked) {
                                    formDataExpiration.forEach((option) => {
                                      if (option.id !== field.id && option.id !== 'date') {
                                        setValue(`expiration.${option.id}` as keyof FormDataProps, false as any);
                                      }
                                    });
                                  }
                                  clearErrors('expiration');
                                  unregister(`expiration.date` as keyof FormDataProps);
                                }}
                              >
                                {field.label}
                              </Checkbox>
                            )}
                        </>
                      )}
                    />
                  </div>
                ))}
                {errors.expiration || edgeCaseForExpirationDate ? (
                  <div className={`col-12 ${expirationOptions.customExp ? 'offset-xl-6 col-xl-6 csrProxy-cust-2' : 'mt-1'}`}>
                    <ErrorMessage
                      data-testid={`expirationError`}
                      errors={errors}                    >
                      {errors.expiration?.date?.message ? errors.expiration.date.message : edgeCaseForExpirationDate ? t('common.requiredSelection', { fieldName: t('csrGuardian.expirationDate').toLowerCase() }) : ''}
                    </ErrorMessage>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <hr className="my-4" />
          {/* purpose of disclosure section */}
          <div className="row mb-4">
            <div className="col-12 d-flex align-items-center mb-2">
              <Paragraph
                level={5}
                variant="bold"
                text={t('csrGuardian.purposeOfDisclosure')}
                className="my-2"
              />
              <Button
                onClick={() => { setIsHelpModalOpen(true) }}
                className="ms-1 svg-18"
                variant="icon_btn"
                data-testid={`PurposeHelpIcon`}
                iconRight={<HelpIcon />}
              />
            </div>
            <div className="col-12">
              <div className="d-flex row align-items-top">
                {formDataPurpose.map((field) => (
                  <div className={field.colClass} key={field.id}>
                    <Controller
                      name={`purpose.${field.id}` as keyof FormDataProps}
                      control={control}
                      rules={{
                        required: field.id === 'disclosureOtherText'
                          ? purposeOptions.disclosureOther ? t('common.requiredField', { fieldName: field.label.toLowerCase() }) : false
                          : !Object.values(purposeOptions).some((value) => value) ? t('common.requiredSelection', { fieldName: t('csrGuardian.purposeOfDisclosure').toLowerCase() }) : undefined,
                        validate: () => {
                          const hasSelection = purposeOptions.disclosureRequest || purposeOptions.disclosureOther;
                          return hasSelection || t('common.requiredSelection', { fieldName: t('csrGuardian.purposeOfDisclosure').toLowerCase() });
                        }
                      }}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <>
                          {field.id === 'disclosureOtherText' ?
                            (
                              purposeOptions.disclosureOther ? (
                                <div className="offset-xl-6 flex-1 col-md-6 col-12 csrProxy-cust-3">
                                  <TextArea
                                    id={field.id}
                                    rows={3}
                                    placeholder={t('common.placeholder', { fieldName: field.label.toLowerCase() })}
                                    maxLength={field.maxLength}
                                    data-testid={`${field.id}Input`}
                                    className={`form-input ${errors.purpose?.[field.id as keyof PurposeProps] ? 'error-border' : ''}`}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                      setValue(`purpose.${field.id}` as keyof FormDataProps, e.target.value, {
                                        shouldValidate: true,
                                      });
                                      clearErrors(`purpose.${field.id}` as keyof FormDataProps);
                                    }}
                                  />
                                </div>
                              ) : null
                            )
                            : (
                              <Checkbox
                                {...field}
                                level={3}
                                id={field.id}
                                type="radio"
                                data-testid={`${field.id}Checkbox`}
                                checked={value || false}
                                onBlur={onBlur}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  const isChecked = (e.target as HTMLInputElement).checked;
                                  onChange(isChecked);
                                  if (isChecked) {
                                    formDataPurpose.forEach((option) => {
                                      if (option.id !== field.id && option.id !== 'disclosureOtherText') {
                                        setValue(`purpose.${option.id}` as keyof FormDataProps, false as any);
                                      }
                                    });
                                  }
                                  clearErrors('purpose');
                                  unregister(`purpose.disclosureOtherText` as keyof FormDataProps);
                                }}                              >
                                {field.label}
                              </Checkbox>
                            )}
                        </>
                      )}
                    />
                  </div>
                ))}
                {errors.purpose || edgeCaseForPurpose ? (
                  <div className={`col-12 ${purposeOptions.disclosureOther ? 'offset-xl-6 col-xl-6 csrProxy-cust-4' : 'mt-1'}`}>
                    <ErrorMessage
                      data-testid={`purposeError`}
                      errors={errors}>
                      {errors.purpose?.disclosureOtherText?.message ? errors.purpose.disclosureOtherText.message : edgeCaseForPurpose ? t('common.requiredSelection', { fieldName: t('csrGuardian.purposeOfDisclosure').toLowerCase() }) : ''}
                    </ErrorMessage>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <hr className="my-4" />
          {/* authorization section */}
          <div className="row mb-md-4 mb-3">
            <div className="col-12">
              <Paragraph
                level={5}
                variant="bold"
                text={t('common.authorizations')}
                className="mt-md-2"
              />
              <Paragraph
                level={3}
                variant="bold"
                text={t('csrGuardian.substanceUseRecords')}
                className="mt-3"
              />
            </div>
            <div className="col-12">
              <div className="d-flex row align-items-top">
                {formDataAbuseAuthorization.map((field) => (
                  <div className={field.colClass} key={field.id}>
                    <Controller
                      name={`abuseAuthorization.${field.id}` as keyof FormDataProps}
                      control={control}
                      rules={{
                        required: !Object.values(abuseAuthorizationOptions).some((value) => value) ? t('common.radioRequired', { fieldName: t('common.authorizations').toLowerCase() }) : undefined,
                      }}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <Checkbox
                          {...field}
                          level={3}
                          id={field.id}
                          type="radio"
                          data-testid={`${field.id}Checkbox`}
                          checked={value || false}
                          onBlur={onBlur}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const isChecked = (e.target as HTMLInputElement).checked;
                            onChange(isChecked);
                            if (isChecked) {
                              resetAbuseAuthorizationOptions(field.id);
                            }
                            clearErrors('abuseAuthorization');
                          }}
                        > {field.label}</Checkbox>
                      )} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="row mt-n2 mb-5">
            <div className="col-12">
              <Paragraph
                level={3}
                variant="bold"
                text={t('csrGuardian.acknowledgementTitle')}
              />
              <div className="d-flex justify-content-start">
                <Checkbox
                  id="acknowledgement"
                  level={3}
                  data-testid="acknowledgementCheckbox"
                  checked={acknowledgement}
                  {...register('acknowledgement' as keyof FormDataProps, {
                    required: t('common.checkboxRequired', { fieldName: t('common.acknowledgementAndRights').toLowerCase() }),
                    onChange: (e) => {
                      clearErrors('acknowledgement');
                      setValue('acknowledgement' as keyof FormDataProps, e.target.checked);
                    }
                  })}
                ><Trans
                    i18nKey="common.checkboxAgree"
                    values={{ fieldName: t('common.acknowledgementAndRights') }}
                    components={{
                      link1: (
                        <Link
                          onClick={() => setIsAcknowledgementModal(true)}
                          className="text-decoration-underline text-nowrap"
                          data-testid="acknowledgementCheckboxLink"
                        />
                      )
                    }}
                  /></Checkbox>
              </div>
              {errors.acknowledgement ? (
                <div className={`col-12 mt-3`}>
                  <ErrorMessage
                    data-testid={`acknowledgementError`}
                    errors={errors}
                  >
                    {errors.acknowledgement.message}
                  </ErrorMessage>
                </div>
              ) : null}
            </div>
          </div>
          {isMobile ? <hr className="mt-3 mb-5" /> : null}
          {/* form action buttons */}
          <div className="row mb-3">
            <div className="col-md-auto col-12 mb-4">
              <Button
                variant="primary_btn"
                type="submit"
                data-testid="submitButton"
                className="btn-md-width"
                disabled={!isValid}
              >
                {t('common.submit')}
              </Button>
            </div>
            <div className="col-md-auto col-12 mb-4">
              <Button
                variant="secondary_btn"
                type="button"
                onClick={handleCancel}
                data-testid="cancelButton"
                className="btn-md-width"
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </form >
      </div >
      {/*modals*/}
      {Object.keys(formDataModals).map((key) => {
        const modal = formDataModals[key as keyof typeof formDataModals];
        return (
          <ModalMultiUse
            key={key}
            iconClose={modal.iconClose}
            header={modal.title}
            body={modal.content}
            variant={modal.variant}
            isMobile={modal.isMobile}
            isOpen={modal.actionOpen()}
            onClose={modal.actionClose}
            data-testid={modal.id}
          />);
      })}
    </>
  );
};

export default CSRProxyForm;